import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, ZoomIn, ZoomOut, RotateCcw, Users, Ticket, Maximize2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { RichElementShape } from './venueRenderer';
import { getCanvasClipPath, getCanvasBorderRadius } from '../../lib/venueShapes';

// ElementShape adapter — uses the shared rich renderer (matches editor styles)
const ElementShape = ({ element, isSelected, isAvailable, onClick }) => (
  <RichElementShape
    element={element}
    isSelected={isSelected}
    isSelectable={isAvailable}
    onClick={onClick}
  />
);


const VenueMapViewer = ({ 
  venue, 
  tiers = [], 
  selectedTierId, 
  onSelectTier,
  showLegend = true,
  className = ''
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeFloor, setActiveFloor] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hover, setHover] = useState(null); // {x, y, element}
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  
  // Track container dimensions so the map scales perfectly to its space and
  // nothing gets cropped — also re-runs on window resize / sidebar collapse.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  
  // Normalize sections from either `sections` (API response from backend) or legacy `elements`.
  // Backend stores position as a nested object: { x, y, width, height } - flatten it for rendering.
  const elements = useMemo(() => {
    const raw = venue?.sections || venue?.elements || [];
    return raw.map(s => {
      const pos = s.position || {};
      return {
        ...s,
        x: pos.x ?? s.x ?? 0,
        y: pos.y ?? s.y ?? 0,
        width: pos.width ?? s.width ?? 100,
        height: pos.height ?? s.height ?? 80,
      };
    });
  }, [venue]);
  
  const floors = venue?.floors?.length ? venue.floors : [{ id: 0, name: 'Principal' }];
  // Use canvas dimensions if provided, otherwise fall back to a sane default
  const canvasWidth = venue?.canvas_width || venue?.canvasWidth || venue?.gridSize || 800;
  const canvasHeight = venue?.canvas_height || venue?.canvasHeight || venue?.gridSize || 500;
  const canvasShape = venue?.canvas_shape || venue?.canvasShape || 'rectangle';
  const canvasPolygon = venue?.canvas_polygon || venue?.canvasPolygon || null;
  const background = venue?.background_preset || venue?.background || 'none';
  
  // Filter elements by active floor (default to floor 0 if not specified)
  const floorElements = useMemo(() => 
    elements.filter(el => (el.floor || 0) === activeFloor),
    [elements, activeFloor]
  );
  
  // Map tiers to elements. Match by linked tier_id, linkedTierId, or by name.
  const tierMap = useMemo(() => {
    const map = {};
    tiers.forEach(tier => {
      const matchingElement = elements.find(el => 
        el.tier_id === tier.id ||
        el.linkedTierId === tier.id ||
        el.name?.toLowerCase() === tier.name?.toLowerCase()
      );
      if (matchingElement) {
        map[matchingElement.id] = {
          tier,
          available: (tier.quantity - (tier.sold || 0)) > 0 && tier.is_active !== false
        };
      }
    });
    return map;
  }, [elements, tiers]);
  
  // Check if element is selectable (has matching tier with availability)
  const isElementSelectable = (element) => {
    const tierInfo = tierMap[element.id];
    return tierInfo?.available || false;
  };
  
  // Check if element is selected
  const isElementSelected = (element) => {
    const tierInfo = tierMap[element.id];
    return tierInfo?.tier?.id === selectedTierId;
  };
  
  // Handle element click
  const handleElementClick = (element) => {
    const tierInfo = tierMap[element.id];
    if (tierInfo?.available && onSelectTier) {
      onSelectTier(tierInfo.tier);
    }
  };
  
  // Zoom controls
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const handleReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  
  // Pan handlers
  const handleMouseDown = (e) => {
    if (e.target.tagName !== 'svg' && e.target.tagName !== 'pattern' && e.target.tagName !== 'rect') return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  
  const handleMouseUp = () => setIsDragging(false);
  
  // Background pattern renderer
  const renderBackground = () => {
    switch (background) {
      case 'minimal-dots':
        return (
          <pattern id="bg-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="0.5" fill="rgba(255,255,255,0.05)"/>
          </pattern>
        );
      case 'neo-grid':
        return (
          <pattern id="bg-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(100,100,255,0.08)" strokeWidth="0.5"/>
          </pattern>
        );
      case 'blueprint':
        return (
          <pattern id="bg-pattern" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(59,130,246,0.1)" strokeWidth="0.5"/>
          </pattern>
        );
      default:
        return null;
    }
  };

  // Lock body scroll + ESC to close while fullscreen modal is open.
  // (Declared BEFORE the conditional early-return below to satisfy rules-of-hooks.)
  useEffect(() => {
    if (!isFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);

  if (!venue) {
    return (
      <div className={`bg-zinc-900/50 rounded-xl p-8 text-center ${className}`}>
        <MapPin className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
        <p className="text-zinc-500">Mapa del venue no disponible</p>
      </div>
    );
  }
  
  // Capitalize first letter for friendly tooltip subtype
  const friendlyType = (t) => {
    const map = {
      section:'Sector', stage:'Escenario', djbooth:'Cabina DJ', bar:'Barra',
      table:'Mesa', lounge:'VIP / Lounge', wall:'Pared', pillar:'Columna',
      area:'Área', entrance:'Entrada', exit:'Salida', bathroom:'Baños',
      stairs:'Escaleras', elevator:'Ascensor', wardrobe:'Guardarropa',
      smoking:'Fumadores', firstaid:'Primeros auxilios', info:'Información',
      parking:'Estacionamiento', foodcourt:'Patio de comidas', wheelchair:'Accesible',
    };
    return map[t] || 'Sector';
  };

  // === Inner Map (re-used in normal + fullscreen modes) ===
  const renderMapInner = (mode = 'normal') => (
    <div className={`bg-zinc-900/80 rounded-xl overflow-hidden ${mode === 'normal' ? className : 'h-full flex flex-col'}`}>
      {/* Header with controls */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin size={16} className="text-emerald-500 flex-shrink-0" />
          <span className="text-sm font-medium truncate">{venue.name}</span>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-7 w-7 p-0" data-testid="venue-viewer-zoom-out">
            <ZoomOut size={14} />
          </Button>
          <span className="text-xs text-zinc-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-7 w-7 p-0" data-testid="venue-viewer-zoom-in">
            <ZoomIn size={14} />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset} className="h-7 w-7 p-0 ml-1" data-testid="venue-viewer-reset">
            <RotateCcw size={14} />
          </Button>
          {mode === 'normal' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(true)}
              className="h-7 w-7 p-0 ml-1 hover:text-emerald-400"
              title="Agrandar mapa"
              data-testid="venue-viewer-fullscreen-open"
            >
              <Maximize2 size={14} />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              className="h-7 w-7 p-0 ml-1 hover:text-red-400"
              title="Cerrar vista ampliada"
              data-testid="venue-viewer-fullscreen-close"
            >
              <X size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* Floor tabs (if multiple floors) */}
      {floors.length > 1 && (
        <div className="flex gap-1 p-2 border-b border-white/10 overflow-x-auto flex-shrink-0">
          {floors.map((floor, idx) => (
            <button
              key={floor.id}
              onClick={() => setActiveFloor(idx)}
              className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-all ${
                activeFloor === idx
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10'
              }`}
            >
              {floor.name}
            </button>
          ))}
        </div>
      )}

      {/* Map canvas */}
      <div
        ref={mode === 'normal' ? containerRef : null}
        className="relative overflow-hidden bg-black/50 flex-1"
        style={mode === 'normal'
          ? { width: '100%', aspectRatio: `${canvasWidth} / ${canvasHeight}`, maxHeight: '70vh' }
          : { width: '100%', minHeight: 0 }
        }
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => { handleMouseMove(e); }}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); setHover(null); }}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {(() => {
            const cw = (mode === 'normal' ? containerSize.width : 0) || 800;
            const ch = (mode === 'normal' ? containerSize.height : 0) || 500;
            // Fullscreen: compute target box from the actual modal dimensions
            // (which are `min(1400, 96vw)` x `min(900, 92vh)` minus header/legend/padding).
            const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
            const h = typeof window !== 'undefined' ? window.innerHeight : 800;
            const fsW = mode === 'fullscreen' ? Math.min(1400, w * 0.96) - 32 : cw;
            const fsH = mode === 'fullscreen' ? Math.min(900, h * 0.92) - 140 : ch;
            const scale = Math.min(fsW / canvasWidth, fsH / canvasHeight, mode === 'fullscreen' ? 2 : 1.2);
            const displayW = canvasWidth * scale;
            const displayH = canvasHeight * scale;
            return (
              <div
                className="relative"
                style={{
                  width: displayW,
                  height: displayH,
                  borderRadius: getCanvasBorderRadius(canvasShape),
                  clipPath: getCanvasClipPath(canvasShape, canvasPolygon),
                  background: '#0a0a0a',
                  border: '1px solid rgba(255,255,255,0.1)',
                  overflow: 'hidden'
                }}
              >
                <svg width="100%" height="100%" className="absolute inset-0" style={{ borderRadius: 'inherit' }}>
                  <defs>{renderBackground()}</defs>
                  {background !== 'none' && <rect width="100%" height="100%" fill="url(#bg-pattern)" style={{ borderRadius: 'inherit' }}/>}
                </svg>

                {floorElements.map(element => {
                  const isSelectable = isElementSelectable(element);
                  const isSelected = isElementSelected(element);
                  const w = element.width * scale;
                  const h = element.height * scale;
                  const tierInfo = tierMap[element.id];
                  return (
                    <div
                      key={element.id}
                      onMouseEnter={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const parentR = e.currentTarget.parentElement?.parentElement?.getBoundingClientRect();
                        setHover({
                          element,
                          tier: tierInfo?.tier,
                          available: tierInfo?.available,
                          x: r.left + r.width / 2 - (parentR?.left || 0),
                          y: r.top - (parentR?.top || 0),
                        });
                      }}
                      onMouseLeave={() => setHover((h) => (h && h.element.id === element.id ? null : h))}
                      style={{
                        position: 'absolute',
                        left: element.x * scale,
                        top: element.y * scale,
                        width: w,
                        height: h,
                        transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
                        transformOrigin: 'center center'
                      }}
                      data-testid={`venue-viewer-element-${element.id}`}
                    >
                      <ElementShape
                        element={{ ...element, width: w, height: h }}
                        isSelected={isSelected}
                        isAvailable={isSelectable}
                        onClick={() => handleElementClick(element)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Hover Tooltip */}
        <AnimatePresence>
          {hover && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.12 }}
              className="absolute z-30 pointer-events-none -translate-x-1/2 -translate-y-full"
              style={{ left: hover.x, top: hover.y - 8 }}
              data-testid="venue-viewer-tooltip"
            >
              <div className="bg-zinc-950/95 backdrop-blur-sm border border-white/15 rounded-lg px-3 py-2 shadow-2xl whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: hover.element.color || '#4F46E5' }}
                  />
                  <p className="text-sm font-bold text-white">{hover.element.name || friendlyType(hover.element.type)}</p>
                </div>
                <p className="text-[10px] text-zinc-400 uppercase tracking-wider mt-0.5">
                  {friendlyType(hover.element.type)}
                </p>
                {hover.tier && (
                  <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-white/10">
                    <div className="flex items-center gap-1 text-xs">
                      <Ticket size={11} className={hover.available ? 'text-emerald-400' : 'text-red-400'} />
                      <span className={hover.available ? 'text-emerald-400' : 'text-red-400'}>
                        {hover.available ? `$${hover.tier.price?.toLocaleString()}` : 'Agotado'}
                      </span>
                    </div>
                    {hover.tier.quantity > 0 && hover.available && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400">
                        <Users size={11} />
                        <span>{Math.max(0, (hover.tier.quantity || 0) - (hover.tier.sold || 0))} disp.</span>
                      </div>
                    )}
                  </div>
                )}
                {!hover.tier && hover.element.capacity > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-white/10 text-xs text-zinc-400">
                    <Users size={11} />
                    <span>Cap. {hover.element.capacity}</span>
                  </div>
                )}
              </div>
              <div className="w-2 h-2 bg-zinc-950/95 border-r border-b border-white/15 rotate-45 mx-auto -mt-1" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Zoom hint */}
        <div className="absolute bottom-2 left-2 text-[10px] text-zinc-600 pointer-events-none">
          Arrastra para mover · Scroll para zoom · Pasá el mouse sobre cada zona
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="p-3 border-t border-white/10 flex flex-wrap gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-3 rounded bg-emerald-500"/>
            <span className="text-zinc-400">Seleccionado</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-3 rounded bg-blue-500 opacity-75"/>
            <span className="text-zinc-400">Disponible</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-3 h-3 rounded bg-zinc-600 opacity-50"/>
            <span className="text-zinc-400">Agotado</span>
          </div>
        </div>
      )}
    </div>
  );

  // Lock body scroll + ESC to close while fullscreen modal is open.
  // (Hook moved above the early-return on `!venue` to comply with rules-of-hooks.)
  return (
    <>
      {renderMapInner('normal')}
      {isFullscreen && createPortal(
        <AnimatePresence>
          <motion.div
            key="venue-fs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 2147483647,
              background: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setIsFullscreen(false); }}
            data-testid="venue-viewer-fullscreen-modal"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{
                width: 'min(1400px, 96vw)',
                height: 'min(900px, 92vh)',
                background: '#18181b',
                borderRadius: '16px',
                boxShadow: '0 25px 80px rgba(0,0,0,0.8)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderMapInner('fullscreen')}
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default VenueMapViewer;
